import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Heart, User, Menu, X, ArrowRight, LogOut, LogIn } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useCatalogData } from '../hooks/useCatalogData';
import { fetchCategoriesTree } from '../services/productService';
import CategoryMenu from './CategoryMenu';
import { isAuthenticated, getCurrentUser, logout } from '../lib/authUtils';
import { toast } from '../hooks/use-toast';

const Header = () => {
  const { getCartCount, wishlist, compareList } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { categories: catalogCategories = [], loading } = useCatalogData();
  const categories = useMemo(() => catalogCategories ?? [], [catalogCategories]);
  const [categoriesTree, setCategoriesTree] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const loadCategoriesTree = async () => {
      setLoadingTree(true);
      try {
        const tree = await fetchCategoriesTree();
        setCategoriesTree(tree);
      } catch (error) {
        console.error('Error loading categories tree:', error);
        setCategoriesTree([]);
      } finally {
        setLoadingTree(false);
      }
    };
    loadCategoriesTree();
  }, []);

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = isAuthenticated();
      setIsLoggedIn(authenticated);
      if (authenticated) {
        setCurrentUser(getCurrentUser());
      } else {
        setCurrentUser(null);
      }
    };
    
    checkAuth();
    // Listen for storage changes (when user logs in/out in another tab)
    window.addEventListener('storage', checkAuth);
    // Listen for custom auth state change event
    window.addEventListener('authStateChanged', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('authStateChanged', checkAuth);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setIsLoggedIn(false);
    setCurrentUser(null);
    setUserMenuOpen(false);
    toast({
      title: 'Logged Out',
      description: 'You have been successfully logged out.',
    });
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  // Close user menu when clicking outside
  useEffect(() => {
    if (!userMenuOpen) return;
    
    const handleClickOutside = (event) => {
      const menuElement = document.querySelector('[data-user-menu]');
      if (menuElement && !menuElement.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-gray-100 border-b">
        <div className="container mx-auto px-4 py-2">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Free Shipping For Orders From $50</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Support 24/7</span>
              <span className="font-semibold text-red-600">800 060-0730</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/logo.png" alt="Kore Tools Logo" className="h-12" />
          </Link>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl mx-8">
            <div className="flex w-full">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for products, brands, or SKU..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-l focus:outline-none focus:border-red-500"
              />
              <button
                type="submit"
                className="bg-red-600 text-white px-6 py-2 rounded-r hover:bg-red-700 transition"
              >
                <Search size={20} />
              </button>
            </div>
          </form>

          {/* Action Icons */}
          <div className="flex items-center space-x-6">
            <Link to="/wishlist" className="relative hover:text-red-600 transition">
              <Heart size={24} />
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {wishlist.length}
                </span>
              )}
            </Link>
            <Link to="/compare" className="relative hover:text-red-600 transition">
              <ArrowRight size={24} />
              {compareList.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {compareList.length}
                </span>
              )}
            </Link>
            <Link to="/cart" className="relative hover:text-red-600 transition">
              <ShoppingCart size={24} />
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </Link>
            
            {/* User Menu */}
            <div className="relative" data-user-menu>
              {isLoggedIn ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center space-x-2 hover:text-red-600 transition"
                  >
                    <User size={24} />
                    {currentUser && (
                      <span className="hidden md:block text-sm font-medium text-gray-700">
                        {currentUser.firstName || currentUser.userName || 'Account'}
                      </span>
                    )}
                  </button>
                  
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50" data-user-menu>
                      {currentUser && (
                        <div className="px-4 py-2 border-b border-gray-200">
                          <p className="text-sm font-semibold text-gray-900">
                            {currentUser.firstName && currentUser.lastName
                              ? `${currentUser.firstName} ${currentUser.lastName}`
                              : currentUser.userName || 'User'}
                          </p>
                          {currentUser.email && (
                            <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>
                          )}
                        </div>
                      )}
                      <Link
                        to="/account"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center space-x-2 px-4 py-2 hover:bg-gray-50 transition text-gray-700"
                      >
                        <User size={16} />
                        <span>My Account</span>
                      </Link>
                      <Link
                        to="/orders"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center space-x-2 px-4 py-2 hover:bg-gray-50 transition text-gray-700"
                      >
                        <ShoppingCart size={16} />
                        <span>My Orders</span>
                      </Link>
                      <div className="border-t border-gray-200 mt-1">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center space-x-2 px-4 py-2 hover:bg-gray-50 transition text-red-600"
                        >
                          <LogOut size={16} />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/customer-login"
                  className="flex items-center space-x-2 hover:text-red-600 transition"
                >
                  <LogIn size={24} />
                  <span className="hidden md:block text-sm font-medium">Login</span>
                </Link>
              )}
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        <form onSubmit={handleSearch} className="md:hidden mt-4">
          <div className="flex w-full">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-l focus:outline-none focus:border-red-500"
            />
            <button
              type="submit"
              className="bg-red-600 text-white px-6 py-2 rounded-r hover:bg-red-700 transition"
            >
              <Search size={20} />
            </button>
          </div>
        </form>
      </div>

      {/* Navigation */}
      <nav className="bg-gray-50 border-t hidden md:block">
        <div className="container mx-auto px-4">
          <ul className="flex space-x-8 py-3">
            <li>
              <Link to="/" className="text-gray-700 hover:text-red-600 transition font-medium">
                Home
              </Link>
            </li>
            <li className="relative group">
              <Link to="/products" className="text-gray-700 hover:text-red-600 transition font-medium">
                Shop
              </Link>
              {/* Mega Menu */}
              <div className="absolute left-0 top-full hidden group-hover:block bg-white shadow-lg border mt-0 w-screen max-w-5xl p-6 rounded-b z-50">
                <CategoryMenu categoriesTree={categoriesTree} loadingTree={loadingTree} />
              </div>
            </li>
            <li>
              <Link to="/quotes" className="text-gray-700 hover:text-red-600 transition font-medium">
                Quotes
              </Link>
            </li>
            <li>
              <Link to="/orders" className="text-gray-700 hover:text-red-600 transition font-medium">
                Orders
              </Link>
            </li>
            <li>
              <Link to="/blog" className="text-gray-700 hover:text-red-600 transition font-medium">
                Blog
              </Link>
            </li>
            <li>
              <Link to="/contact" className="text-gray-700 hover:text-red-600 transition font-medium">
                Contact
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <nav className="container mx-auto px-4 py-4">
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="block text-gray-700 hover:text-red-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/products"
                  className="block text-gray-700 hover:text-red-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Shop
                </Link>
              </li>
              <li>
                <Link
                  to="/quotes"
                  className="block text-gray-700 hover:text-red-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Quotes
                </Link>
              </li>
              <li>
                <Link
                  to="/orders"
                  className="block text-gray-700 hover:text-red-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Orders
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="block text-gray-700 hover:text-red-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="block text-gray-700 hover:text-red-600 transition font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </Link>
              </li>
              <li className="border-t pt-3 mt-3">
                {isLoggedIn ? (
                  <>
                    <Link
                      to="/account"
                      className="block text-gray-700 hover:text-red-600 transition font-medium mb-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      My Account
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="block w-full text-left text-red-600 hover:text-red-700 transition font-medium"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to="/customer-login"
                    className="block text-gray-700 hover:text-red-600 transition font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login / Sign Up
                  </Link>
                )}
              </li>
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
